'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface MaturityLevel {
  level: number;
  name: string;
  description: string;
  criteria: string[];
}

const maturityLevels: MaturityLevel[] = [
  { level: 1, name: 'Initial', description: 'Manual, ad-hoc processes', criteria: ['Manual deployments', 'No automation', 'Inconsistent processes', 'No monitoring'] },
  { level: 2, name: 'Managed', description: 'Basic automation in place', criteria: ['Basic CI pipeline', 'Automated tests run', 'Version control used', 'Manual deployments'] },
  { level: 3, name: 'Defined', description: 'Standardized automated processes', criteria: ['Automated deployments', 'Code review required', 'Quality gates enforced', 'Basic monitoring'] },
  { level: 4, name: 'Quantitatively Managed', description: 'Measured and controlled', criteria: ['Metrics-driven decisions', 'Performance tracking', 'Security scans automated', 'Rollback capability'] },
  { level: 5, name: 'Optimizing', description: 'Continuous improvement culture', criteria: ['Feature flags', 'Canary/blue-green deployments', 'Auto-rollback', 'Zero-downtime', 'Chaos engineering'] },
];

export default function CICDMaturityGraderPage() {
  const [answers, setAnswers] = useState<Record<string, number>>({
    'build1': 3, 'build2': 3, 'build3': 3,
    'test1': 3, 'test2': 3, 'test3': 3,
    'deploy1': 3, 'deploy2': 3, 'deploy3': 3,
    'monitor1': 3, 'monitor2': 3,
    'security1': 3, 'security2': 3,
  });
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'cicd-maturity-grader',
    toolCategory: 'maturity',
  });

  const questions = [
    { id: 'build1', category: 'Build Automation', question: 'Build process is fully automated' },
    { id: 'build2', category: 'Build Automation', question: 'Builds trigger automatically on commits' },
    { id: 'build3', category: 'Build Automation', question: 'Build artifacts stored and versioned' },
    { id: 'test1', category: 'Testing', question: 'Automated tests run in pipeline' },
    { id: 'test2', category: 'Testing', question: 'Test coverage tracked and enforced' },
    { id: 'test3', category: 'Testing', question: 'Tests block bad deployments' },
    { id: 'deploy1', category: 'Deployment', question: 'Deployments are fully automated' },
    { id: 'deploy2', category: 'Deployment', question: 'Zero-downtime deployments achieved' },
    { id: 'deploy3', category: 'Deployment', question: 'Rollback capability exists' },
    { id: 'monitor1', category: 'Monitoring', question: 'Deployment monitoring in place' },
    { id: 'monitor2', category: 'Monitoring', question: 'Automated alerts configured' },
    { id: 'security1', category: 'Security', question: 'Security scans automated' },
    { id: 'security2', category: 'Security', question: 'Vulnerability blocking enabled' },
  ];

  const maturityScore = useMemo(() => {
    const avg = Object.values(answers).reduce((sum, val) => sum + val, 0) / Object.values(answers).length;
    const level = Math.min(5, Math.max(1, Math.round(avg)));
    return { score: avg, level, info: maturityLevels[level - 1] };
  }, [answers]);

  const categoryScores = useMemo(() => {
    const categories = ['Build Automation', 'Testing', 'Deployment', 'Monitoring', 'Security'];
    return categories.map(cat => {
      const catQuestions = questions.filter(q => q.category === cat);
      const avg = catQuestions.reduce((sum, q) => sum + answers[q.id], 0) / catQuestions.length;
      return { category: cat, score: avg };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = categoryScores
      .filter(cat => cat.score < 3)
      .map(cat => ({
        id: cat.category.toLowerCase().replace(/\s+/g, '-'),
        title: `${cat.category} at Level ${Math.round(cat.score)}`,
        severity: cat.score < 2 ? 'critical' as const : 'high' as const,
        description: `Average score: ${cat.score.toFixed(1)}/5`,
      }));

    const recs = categoryScores
      .filter(cat => cat.score < 5)
      .sort((a, b) => a.score - b.score)
      .slice(0, 5)
      .map(cat => ({
        id: cat.category.toLowerCase().replace(/\s+/g, '-'),
        title: `Improve ${cat.category}`,
        priority: cat.score < 2 ? 'high' as const :
                  cat.score < 3 ? 'high' as const : 'medium' as const,
        description: `Current score: ${cat.score.toFixed(1)}/5. Target: ${Math.min(5, Math.ceil(cat.score) + 1)}/5`,
      }));

    const scores: Record<string, number> = {};
    categoryScores.forEach(cat => {
      scores[cat.category.toLowerCase().replace(/\s+/g, '-')] = (cat.score / 5) * 100;
    });

    const assessment = await saveResult({
      name: `CI/CD Maturity - ${new Date().toLocaleDateString()}`,
      score_overall: (maturityScore.score / 5) * 100,
      scores,
      answers,
      gaps,
      recommendations: recs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🔄</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              CI/CD Maturity Grader
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Assess your continuous integration and deployment maturity level
          </p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">CI/CD Maturity Grader</span>
        </nav>

        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this grader</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Rate each capability from 1 (not implemented) to 5 (fully optimized)</li>
            <li>Review your overall maturity level and category scores</li>
            <li>Follow recommendations to reach the next maturity level</li>
          </ol>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {questions.map(q => (
              <div key={q.id} className="bg-surface rounded-lg border border-border p-6">
                <div className="mb-2">
                  <span className="text-accent text-sm font-semibold">{q.category}</span>
                </div>
                <div className="mb-3 text-foreground">{q.question}</div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(val => (
                    <button
                      key={val}
                      onClick={() => setAnswers({ ...answers, [q.id]: val })}
                      className={`px-4 py-2 rounded font-semibold ${
                        answers[q.id] === val
                          ? 'bg-accent text-accent-foreground'
                          : 'bg-background border border-border text-foreground hover:bg-accent-muted'
                      } transition-colors`}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6 sticky top-4">
              <h3 className="text-xl font-bold text-foreground mb-4">Maturity Level</h3>
              <div className="text-center py-6 mb-4">
                <div className="text-6xl font-bold text-accent mb-2">
                  Level {maturityScore.level}
                </div>
                <div className="text-2xl text-foreground mb-2">{maturityScore.info.name}</div>
                <div className="text-sm text-foreground-secondary">{maturityScore.info.description}</div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-foreground mb-3">Characteristics:</h4>
                <ul className="space-y-2">
                  {maturityScore.info.criteria.map((c, i) => (
                    <li key={i} className="text-sm text-foreground-secondary">• {c}</li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-border-muted pt-4">
                <h4 className="font-semibold text-foreground mb-3">Category Scores:</h4>
                <div className="space-y-2">
                  {categoryScores.map(cat => (
                    <div key={cat.category} className="flex justify-between text-sm">
                      <span className="text-foreground-secondary">{cat.category}</span>
                      <span className="text-accent font-bold">{cat.score.toFixed(1)}/5</span>
                    </div>
                  ))}
                </div>
              </div>

              {maturityScore.level < 5 && (
                <div className="mt-6 p-4 bg-accent-muted rounded">
                  <h4 className="font-semibold text-accent mb-2">Next Level:</h4>
                  <div className="text-xs text-foreground-secondary">
                    {maturityLevels[maturityScore.level].name}
                  </div>
                  <div className="text-xs text-foreground-tertiary mt-1">
                    {maturityLevels[maturityScore.level].description}
                  </div>
                </div>
              )}

              {/* Save to Command Center */}
              {commandCenterEnabled && !saved && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <button
                    onClick={handleSaveToCommandCenter}
                    disabled={saving}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save to Command Center'}
                  </button>
                </div>
              )}
              {saved && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved to Command Center
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
